import * as React from 'react'

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { mapboxFeatureToAddress } from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '~/client/src/shared/components/shadcn-ui/combobox'
import BaseStruxhubInput, {
  ISharedProps,
} from '~/client/src/shared/components/StruxhubInputs/BaseStruxhubInput'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'

import { highlightText } from '../../../utils/highlightText'

// localization: no text to translate

interface IProps extends ISharedProps {
  fullAddressLabel: string
  items: IMapBoxFeature[]
  fullAddressClassName?: string
  onItemSelect: (
    item: IMapBoxFeature,
    event?: React.SyntheticEvent<HTMLElement, Event>,
  ) => void
  onQueryChange: (
    query: string,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void
}

@observer
export default class StruxhubMapboxAddressInput extends React.Component<IProps> {
  @observable private query: string = ''

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { fullAddressLabel, items, fullAddressClassName } = this.props
    const visibleItems = this.query.trim() ? items : []

    return (
      <BaseStruxhubInput {...this.props}>
        {(_, isInFocus, onFocus, onBlur) => {
          return isInFocus ? (
            <Combobox
              items={visibleItems}
              filter={null}
              autoHighlight
              itemToStringLabel={mapBoxOnRenderInputValue}
              inputValue={this.query}
              onInputValueChange={this.handleQueryChange}
              onValueChange={this.onItemSelect.bind(this, onBlur)}
            >
              <ComboboxInput
                autoFocus
                showTrigger={false}
                className="tw:h-10 tw:w-full tw:rounded-[4px] tw:border-border tw:bg-muted"
                onBlur={this.handleInputBlur.bind(this, onBlur)}
              />
              <ComboboxContent className="tw:data-empty:hidden">
                <ComboboxList>
                  {(item: IMapBoxFeature) => (
                    <ComboboxItem key={item.id} value={item}>
                      {this.renderItem(item)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          ) : (
            <div
              className={classList({
                [fullAddressClassName]: !!fullAddressClassName,
                'tw:flex tw:items-center tw:whitespace-nowrap tw:w-full tw:bg-muted tw:outline-none tw:rounded-[4px] tw:p-2 tw:h-[40px] tw:border tw:border-solid tw:border-border tw:overflow-hidden': true,
              })}
              onClick={this.handleFocus.bind(this, onFocus)}
            >
              <span
                className="tw:flex-1 tw:text-body-lg tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis"
                title={fullAddressLabel}
              >
                {fullAddressLabel}
              </span>
            </div>
          )
        }}
      </BaseStruxhubInput>
    )
  }

  @action.bound
  private handleQueryChange(
    query: string,
    eventDetails: ComboboxPrimitive.Root.ChangeEventDetails,
  ) {
    if (eventDetails.reason !== 'input-change') {
      return
    }
    this.query = query
    this.props.onQueryChange(query)
  }

  @action.bound
  private handleFocus(onFocus: () => void) {
    this.query = ''
    onFocus()
  }

  // Base UI briefly focuses the suggestion list and hands focus back to the
  // input; unmounting on that blur would drop the editor mid-typing.
  private handleInputBlur(
    onBlurFn: () => void,
    event: React.FocusEvent<HTMLInputElement>,
  ) {
    const next = event.relatedTarget as HTMLElement | null
    if (next?.closest('[data-slot="combobox-content"]')) {
      return
    }
    onBlurFn()
  }

  private onItemSelect(onBlurFn: () => void, item: IMapBoxFeature | null) {
    if (!item) {
      return
    }
    this.props.onItemSelect?.(item)
    onBlurFn()
  }

  private renderItem(item: IMapBoxFeature): JSX.Element {
    const addr = mapboxFeatureToAddress(item)
    const label = [addr.city, addr.state, addr.country]
      .filter(Boolean)
      .join(' ')

    return (
      <>
        <span className="tw:min-w-0 tw:flex-1 tw:truncate">
          {highlightText(addr.address, this.query)}
        </span>
        {label && (
          <span className="tw:shrink-0 tw:text-muted-foreground">{label}</span>
        )}
      </>
    )
  }
}

function mapBoxOnRenderInputValue(item: IMapBoxFeature): string {
  return item.place_name
}
